"use client";

import { useEffect, useMemo, useState } from "react";
import { useSWRConfig } from "swr";
import { API_BASE_URL } from "@/lib/config";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Cpu, FileText, Sparkles, CircleCheckBig, Users, Code2, Layers, Diamond, ChevronDown, Maximize2, X, Loader2, Workflow, Check } from "lucide-react";
import { useConsultationEvents, type PlanStep } from "@/hooks/use-consultation-events";
import { getViewModeChrome } from "../view-mode-chrome";

const phaseConfig: Record<string, { text: string; icon: any; iconColor: string }> = {
  discovery: {
    text: "Discovering relevant tools...",
    icon: Search,
    iconColor: "text-muted-foreground",
  },
  building_docs: {
    text: "Building documentation...",
    icon: FileText,
    iconColor: "text-muted-foreground",
  },
  planning: {
    text: "Planning agent is reasoning...",
    icon: Cpu,
    iconColor: "text-violet-400",
  },
  enriching: {
    text: "Finalizing plan...",
    icon: Sparkles,
    iconColor: "text-foreground",
  },
  complete: {
    text: "Plan complete",
    icon: CircleCheckBig,
    iconColor: "text-green-500",
  },
};

const fallbackPhase = {
  text: "Consulting planning agent...",
  icon: Cpu,
  iconColor: "text-violet-400",
};

// -- Helper: extract steps from any tool result shape --
function extractStepsFromResult(result: any): PlanStep[] | null {
  if (!result) return null;

  const mapSteps = (arr: any[]): PlanStep[] =>
    arr.map((s: any) => ({
      id: s.id ?? s.description?.slice(0, 20) ?? "",
      type: s.type ?? "code",
      description: s.description ?? "",
      status: s.status ?? "pending",
      agents: s.agents,
      functions: s.functions,
      templates: s.templates,
    }));

  const tryExtract = (data: any): PlanStep[] | null => {
    if (!data) return null;
    // data.plan is an array directly (update_plan / get_plan)
    if (Array.isArray(data?.plan) && data.plan.length > 0) {
      return mapSteps(data.plan);
    }
    // data.plan is a JSON string (consult_planning_agent)
    if (typeof data?.plan === "string") {
      try {
        const parsed = JSON.parse(data.plan);
        if (Array.isArray(parsed?.steps) && parsed.steps.length > 0) {
          return mapSteps(parsed.steps);
        }
        if (Array.isArray(parsed) && parsed.length > 0) {
          return mapSteps(parsed);
        }
      } catch {}
    }
    // data.plan is an object with .steps (already parsed)
    if (data?.plan && typeof data.plan === "object" && !Array.isArray(data.plan)) {
      if (Array.isArray(data.plan.steps) && data.plan.steps.length > 0) {
        return mapSteps(data.plan.steps);
      }
    }
    // data.steps directly
    if (Array.isArray(data?.steps) && data.steps.length > 0) {
      return mapSteps(data.steps);
    }
    return null;
  };

  // Direct result
  let steps = tryExtract(result);
  if (steps) return steps;
  // Nested in content[0].text JSON
  if (result.content && Array.isArray(result.content)) {
    const textContent = result.content.find((c: any) => c.type === "text");
    if (textContent?.text) {
      try { steps = tryExtract(JSON.parse(textContent.text)); } catch {}
      if (steps) return steps;
    }
  }
  // String result
  if (typeof result === "string") {
    try { steps = tryExtract(JSON.parse(result)); } catch {}
    if (steps) return steps;
  }
  return null;
}

// -- Expanded Modal Step Row (Bigger, sharp, minimalistic) --
function ExpandedStepRow({ step, idx }: { step: PlanStep; idx: number }) {
  const StepIcon = step.type === "sub_agent" ? Users
    : step.type === "fund_agent" ? FileText
    : step.type === "code" ? Code2
    : Layers;
  
  const badgeColors = step.type === "sub_agent" ? "text-violet-400 border-violet-400/30"
    : step.type === "fund_agent" ? "text-amber-400 border-amber-400/30"
    : step.type === "code" ? "text-emerald-400 border-emerald-400/30"
    : "text-sky-400 border-sky-400/30";

  const badgeLabel = step.type === "sub_agent" ? "agents"
    : step.type === "fund_agent" ? "fundamentals"
    : step.type;

  return (
    <motion.div
      key={step.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: idx * 0.05, ease: [0.25, 1, 0.5, 1] }}
      className="group"
    >
      <div className="flex flex-col py-4 border-b border-border/40 group-last:border-0">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-6 h-6 mt-0.5 flex items-center justify-center">
            {step.status === "completed" ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <CircleCheckBig className="w-6 h-6 text-green-500" strokeWidth={1.5} />
              </motion.div>
            ) : step.status === "in_progress" ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Loader2 className="w-5 h-5 text-foreground/70" strokeWidth={2} />
              </motion.div>
            ) : (
              <span className="text-[13px] font-medium text-muted-foreground/50">
                {String(idx + 1).padStart(2, '0')}
              </span>
            )}
          </div>
          
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border ${badgeColors} bg-background text-[11px] font-medium uppercase tracking-wider`}>
                <StepIcon className="w-3 h-3" strokeWidth={2} />
                {badgeLabel}
              </span>
            </div>
            
            <p className={`text-[15px] leading-relaxed tracking-tight ${
              step.status === "completed" ? "text-muted-foreground" : "text-foreground/90 font-medium"
            }`}>
              {step.description}
            </p>

            {/* Show agents automatically in expanded view */}
            {step.agents && step.agents.length > 0 && (
              <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-muted-foreground/10">
                {step.agents.map((agent, aIdx) => (
                  <div key={aIdx} className="flex items-start gap-2.5">
                    <Diamond className="w-2.5 h-2.5 mt-1 text-muted-foreground/40 flex-shrink-0" strokeWidth={2} />
                    <span className="text-[13px] text-muted-foreground/80 leading-relaxed">
                      {agent.task}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// -- ExpandedPlanModal: sharp wealthy minimalistic overlay --
function ExpandedPlanModal({ steps, onClose, completedCount, totalSteps }: {
  steps: PlanStep[];
  onClose: () => void;
  completedCount: number;
  totalSteps: number;
}) {
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[85vh] flex flex-col bg-card border border-border shadow-2xl rounded-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex-shrink-0 px-8 py-6 border-b border-border/50 bg-card/50 backdrop-blur">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center border border-border/50">
                <Workflow className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-lg font-medium text-foreground tracking-tight">Analysis Blueprint</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">
                    {completedCount} of {totalSteps} steps complete
                  </span>
                  <span className="text-muted-foreground/30">•</span>
                  <span className="text-sm font-medium text-green-500">{progressPercent}%</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>

          <div className="mt-6 h-1 w-full bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-foreground"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-8 py-2">
          <div className="max-w-2xl mx-auto">
            <AnimatePresence mode="popLayout">
              {steps.map((step, idx) => (
                <ExpandedStepRow key={step.id} step={step} idx={idx} />
              ))}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// -- StepRow: single step with optional expand for sub_agent agents --
function StepRow({ step, idx }: { step: PlanStep; idx: number }) {
  const [expanded, setExpanded] = useState(false);
  const StepIcon = step.type === "sub_agent" ? Users
    : step.type === "fund_agent" ? FileText
    : step.type === "code" ? Code2
    : Layers;
  const badgeLabel = step.type === "sub_agent" ? "agents"
    : step.type === "fund_agent" ? "fundamentals"
    : step.type;
  const hasExpandable = step.type === "sub_agent" || step.type === "fund_agent";

  return (
    <motion.div
      key={step.id}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, delay: idx * 0.04 }}
    >
      <div className="flex flex-col">
        {/* Row: spinner + badge + text + chevron — all centered */}
        <div className="flex items-center gap-1.5 min-h-[18px]">
          <div className="flex-shrink-0 w-3.5 h-3.5 flex items-center justify-center">
            {step.status === "completed" ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
              >
                <CircleCheckBig className="w-3.5 h-3.5 text-green-500" />
              </motion.div>
            ) : (
              <span className={`text-[10px] font-semibold tabular-nums leading-none ${
                step.status === "in_progress" ? "text-foreground/70" : "text-muted-foreground/40"
              }`}>
                {idx + 1}
              </span>
            )}
          </div>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-muted/50 text-foreground/90 flex-shrink-0">
            <StepIcon className="w-2.5 h-2.5" strokeWidth={2} />
            {badgeLabel}
          </span>
          <span
            className={`text-xs leading-tight flex-1 min-w-0 ${
              expanded ? "" : "line-clamp-1"
            } ${
              step.status === "completed"
                ? "text-muted-foreground/50"
                : step.status === "in_progress"
                ? "text-foreground/90 font-medium"
                : "text-foreground/70"
            }`}
          >
            {step.description}
          </span>
          {hasExpandable && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex-shrink-0 p-0 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
            >
              <ChevronDown
                className={`w-3 h-3 transition-transform duration-150 ${expanded ? "rotate-180" : ""}`}
                strokeWidth={2}
              />
            </button>
          )}
        </div>

        {/* Agents list — stacked below the row when expanded */}
        {expanded && step.agents && step.agents.length > 0 && (
          <div className="mt-1 pl-5 space-y-0.5">
            <AnimatePresence mode="popLayout">
              {step.agents.map((agent, aIdx) => (
                <motion.div
                  key={`${step.id}-agent-${aIdx}`}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15, delay: aIdx * 0.05 }}
                  className="flex items-start gap-1.5"
                >
                  <Diamond className="w-2 h-2 mt-[3px] text-muted-foreground/30 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-[10px] text-muted-foreground/50 leading-tight">
                    {agent.task}
                  </span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
}

interface PlanDisplayProps {
  sessionId?: string;
  isComplete?: boolean;
  result?: any;
  mode?: "streaming" | "progress";
  viewMode?: string;
}

export function ConsultPlanningAgentLoading({ sessionId, isComplete = false, result, mode = "streaming", viewMode}: PlanDisplayProps) {
  const chrome = getViewModeChrome(viewMode);
  const { consultation } = useConsultationEvents();
  const { mutate } = useSWRConfig();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isComplete && sessionId) {
      mutate(`${API_BASE_URL}/strategies/strategy-plan-by-sessionID/${sessionId}`);
    }
  }, [isComplete, sessionId, mutate]);

  // Determine steps: prefer SSE events if present, fall back to parsed result (reload/success/progress)
  const resultSteps = useMemo(() => extractStepsFromResult(result), [result]);
  // In progress mode (update_plan), always prefer resultSteps — consultation.steps are stale from planning SSE
  const steps = (mode === "progress" && resultSteps && resultSteps.length > 0)
    ? resultSteps
    : (consultation.steps.length > 0 ? consultation.steps : (resultSteps ?? []));

  const done = isComplete || consultation.phase === "complete" || mode === "progress";
  const hasSteps = steps.length > 0;

  // In progress mode, derive progress stats
  const completedCount = steps.filter(s => s.status === "completed").length;
  const totalSteps = steps.length;
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  // Phase config (only used when no steps yet in streaming mode)
  const config = done
    ? phaseConfig["complete"]
    : consultation.phase
      ? phaseConfig[consultation.phase] ?? fallbackPhase
      : fallbackPhase;
  const Icon = config.icon;

  const showPhase = mode === "streaming" && !hasSteps;
  const showDiscovery = consultation.discoveries.length > 0 && !hasSteps;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="w-full -mb-2"
      >
        <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
          {/* Progress bar for update_plan mode */}
          {mode === "progress" && totalSteps > 0 && (
            <div className="h-0.5 bg-muted/30">
              <motion.div
                className="h-full bg-green-500/60"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>
          )}

          <div className="px-3 py-2 flex items-center gap-3 min-w-0">
            {/* Header / Phase Section */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Phase header — only when no steps yet */}
                {showPhase && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={consultation.phase ?? "init"}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center gap-2.5"
                    >
                      <motion.div
                        animate={{ scale: [1, 1.1, 1], opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        className="flex items-center justify-center shrink-0"
                      >
                        <Icon className={`w-4 h-4 ${config.iconColor}`} strokeWidth={2} />
                      </motion.div>
                      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                        <span className={`text-xs font-medium ${chrome.text} leading-tight`}>{config.text}</span>
                        {consultation.phase === "discovery" && consultation.discoveryTotal > 0 && (
                          <span className={`text-[11px] ${chrome.subtext} truncate`}>
                            {consultation.discoveryCompleted} of {consultation.discoveryTotal} agents complete
                          </span>
                        )}
                        {consultation.phase === "building_docs" && (consultation.fnCount > 0 || consultation.scrCount > 0) && (
                          <span className={`text-[11px] ${chrome.subtext} truncate`}>
                            {consultation.fnCount} functions, {consultation.scrCount} screeners
                          </span>
                        )}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                )}

                {/* Steps header when steps exist */}
                {hasSteps && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className={`text-xs font-medium ${chrome.text}`}>
                      {done ? "Analysis Plan" : "Building plan"}
                    </span>
                    {totalSteps > 0 && (
                      <span className="text-[10px] text-muted-foreground/70">
                        {completedCount}/{totalSteps}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Expand Button */}
              {hasSteps && steps.length > 0 && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex-shrink-0 ml-2 p-1.5 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-muted/50 transition-colors"
                  title="View full plan"
                >
                  <Maximize2 className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              )}
            </div>

            {/* Discovery feed */}
            {showDiscovery && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.2 }}
                className="mt-2 flex flex-wrap gap-1.5"
              >
                {consultation.discoveries.map((d, i) => (
                  <motion.span
                    key={`${d.domain}-${i}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.15 }}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-muted/40 text-muted-foreground"
                  >
                    <span className="font-medium">{d.domain}</span>
                    {d.tools_found.length > 0 && (
                      <span className="text-muted-foreground/50">
                        · {d.tools_found.length} tool{d.tools_found.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </motion.span>
                ))}
              </motion.div>
            )}

            {/* Steps list */}
            {hasSteps && (
              <div className="space-y-1">
                <AnimatePresence mode="popLayout">
                  {steps.map((step, idx) => (
                    <StepRow key={step.id} step={step} idx={idx} />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Expanded Modal Overlay */}
      <AnimatePresence>
        {isModalOpen && hasSteps && (
          <ExpandedPlanModal
            steps={steps}
            onClose={() => setIsModalOpen(false)}
            completedCount={completedCount}
            totalSteps={totalSteps}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// Re-export for backward compat with update_plan / get_plan routing
export const PlanDisplay = ConsultPlanningAgentLoading;
